import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _AbListHelper;
/*
 *                     NOTICE
 *            COPYRIGHT 2021 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { AbIterable } from "abweb/collection/AbIterable";
import { ArrayUtils } from "abweb/utils/ArrayUtils";
import { TypeUtils } from "abweb/utils/TypeUtils";
const listModelGenerator = function* (model) {
  for (let i = 0; i < model.size; i++) {
    yield model.itemAt(i);
  }
};
export class AbListHelper {
  constructor(data) {
    _defineProperty(this, "_data", void 0);
    _defineProperty(this, "itemAt", index => {
      if (index < 0 || index >= this.size || !Number.isInteger(index)) throw new Error(`Index must be whole number between 0 and size - 1. '${index}' is invalid.`);
      return this._data.itemAt(index);
    });
    _defineProperty(this, "map", mapFunction => {
      return AbListHelper.map(this, mapFunction);
    });
    _defineProperty(this, "findIndexOf", v => {
      if (this.indexOf) return this.indexOf(v);
      return this.findIndex(item => TypeUtils.equals(v, item, this._data.keyFunction));
    });
    _defineProperty(this, "findIndex", predicate => {
      for (let i = 0; i < this.size; i++) {
        if (predicate(this.itemAt(i), i)) {
          return i;
        }
      }
      return -1;
    });
    _defineProperty(this, "findLastIndex", predicate => {
      for (let i = this.size - 1; i >= 0; i--) {
        if (predicate(this.itemAt(i), i)) {
          return i;
        }
      }
      return -1;
    });
    _defineProperty(this, "findLast", predicate => {
      const index = this.findLastIndex(predicate);
      return index === -1 ? undefined : this.itemAt(index);
    });
    _defineProperty(this, "iterator", () => {
      return this[Symbol.iterator]();
    });
    _defineProperty(this, "filter", predicate => {
      return AbListHelper.filter(this, predicate);
    });
    _defineProperty(this, "filterType", predicate => {
      return AbListHelper.filterType(this, predicate);
    });
    _defineProperty(this, "first", () => {
      if (this.size > 0) return this.itemAt(0);
      return undefined;
    });
    _defineProperty(this, "last", () => {
      if (this.size > 0) return this.itemAt(this.size - 1);
      return undefined;
    });
    /**
     * Returns a view of the range [startIndex, endIndex). Does not copy, so if the underlying model changes, the slice changes.
     * endIndex is exclusive and defaults to the size of the model.
     */
    _defineProperty(this, "slice", (startIndex, endIndex) => {
      return AbListHelper.slice(this, startIndex, endIndex);
    });
    _defineProperty(this, "reverse", () => {
      return AbListHelper.reverse(this);
    });
    /**
     * Supports the Iterable interface.
     */
    _defineProperty(this, Symbol.iterator, () => {
      return listModelGenerator(this._data);
    });
    this._data = data;
  }
  get size() {
    return this._data.size;
  }
  toArray() {
    return [...this];
  }
  get indexOf() {
    return this._data.indexOf;
  }
  static from(data, keyFunction) {
    if (ArrayUtils.isReadonlyArrayLike(data)) {
      return AbListHelper.fromArray(data, keyFunction);
    }
    return new AbListHelper(data);
  }
  static toArray(model) {
    return AbListHelper.from(model).toArray();
  }
  static map(model, mapFunction) {
    return AbListHelper.from({
      get size() {
        return model.size;
      },
      itemAt: index => {
        return mapFunction(model.itemAt(index), index);
      }
    });
  }
}
_AbListHelper = AbListHelper;
_defineProperty(AbListHelper, "fromArray", (array, keyFunction) => {
  return new _AbListHelper({
    get size() {
      return array.length;
    },
    itemAt: index => {
      return array[index];
    },
    keyFunction: keyFunction
  });
});
_defineProperty(AbListHelper, "filter", (model, predicate) => {
  return AbIterable.filter(_AbListHelper.from(model), predicate);
});
_defineProperty(AbListHelper, "filterType", (model, predicate) => {
  return AbIterable.filterType(_AbListHelper.from(model), predicate);
});
/**
 * Returns a view of the range [startIndex, endIndex). Does not copy, so if the underlying model changes, the slice changes.
 * endIndex is exclusive and defaults to the size of the model.
 */
_defineProperty(AbListHelper, "slice", (model, startIndex, endIndex) => {
  if (endIndex !== undefined && endIndex < startIndex) throw new Error("endIndex should be >= startIndex");
  return _AbListHelper.from({
    get size() {
      return (endIndex ?? model.size) - startIndex;
    },
    itemAt: index => {
      return model.itemAt(index + startIndex);
    },
    get indexOf() {
      if (!model.indexOf) return undefined;
      const indexOf = model.indexOf;
      return item => {
        const index = indexOf(item) - startIndex;
        if (index < 0 || index >= (endIndex ?? model.size) - startIndex) return -1;
        return index;
      };
    },
    get keyFunction() {
      return model.keyFunction;
    }
  });
});
_defineProperty(AbListHelper, "reverse", model => {
  return _AbListHelper.from({
    get size() {
      return model.size;
    },
    itemAt: index => {
      return model.itemAt(model.size - index - 1);
    },
    get indexOf() {
      if (!model.indexOf) return undefined;
      const indexOf = model.indexOf;
      return item => {
        const index = indexOf(item);
        if (index < 0 || index >= model.size) return -1;
        return model.size - index - 1;
      };
    },
    get keyFunction() {
      return model.keyFunction;
    }
  });
});
/**
 * Make a new list helper with one item shifted from an old index to a new index
 */
_defineProperty(AbListHelper, "moveListItem", (model, reorderEvent) => {
  const {
    fromIndex,
    toIndex
  } = reorderEvent;
  if (fromIndex < 0 || fromIndex >= model.size || toIndex < 0 || fromIndex >= model.size) throw new Error(`Indices must be >= 0 and < length of array (${model.size}). fromIndex: ${fromIndex}, toIndex: ${toIndex}`);
  if (fromIndex === toIndex) return _AbListHelper.from(model);
  return _AbListHelper.from({
    get size() {
      return model.size;
    },
    itemAt: index => {
      if (index === toIndex) return model.itemAt(fromIndex);
      const inMiddle = index >= Math.min(fromIndex, toIndex) && index <= Math.max(fromIndex, toIndex);
      if (!inMiddle) return model.itemAt(index);
      const isShiftRight = fromIndex > toIndex;
      return model.itemAt(index + (isShiftRight ? -1 : 1));
    },
    get indexOf() {
      if (!model.indexOf) return undefined;
      const indexOf = model.indexOf;
      return item => {
        const index = indexOf(item);
        if (index === fromIndex) return toIndex;
        const inMiddle = index >= Math.min(fromIndex, toIndex) && index <= Math.max(fromIndex, toIndex);
        if (!inMiddle) return index;
        const isShiftRight = fromIndex > toIndex;
        return index + (isShiftRight ? 1 : -1);
      };
    },
    get keyFunction() {
      return model.keyFunction;
    }
  });
});
/**
 * Return a list model of integers incrementing from start (inclusive) to end (exclusive).
 * Will error if increment is 0 or any of the provided numbers are not integers.
 * @param {number} start
 * @param {number} end
 * @param {number} increment (default = 1)
 */
_defineProperty(AbListHelper, "range", (start, end, increment = 1) => {
  if (increment === 0) {
    throw new Error("Cannot create range with increment = 0");
  }
  if (!Number.isInteger(start) || !Number.isInteger(end) || !Number.isInteger(increment)) {
    throw new Error("Cannot create range with non-integer parameters");
  }
  if (increment > 0 && start >= end || increment < 0 && start <= end) {
    return _AbListHelper.from([]);
  }
  const size = Math.ceil(Math.abs(end - start) / Math.abs(increment));
  return _AbListHelper.from({
    get size() {
      return size;
    },
    itemAt: index => {
      return start + index * increment;
    },
    indexOf: item => {
      const moved = item - start;
      const index = moved / increment;
      if (!Number.isInteger(index)) return -1;
      if (index < 0 || index >= size) return -1;
      return index;
    }
  });
});
/**
 * Finds the closest value to the target given a sorted list (low to high). If the model does not contain sorted data, behavior is undefined.
 */
_defineProperty(AbListHelper, "findClosestValue", (model, target) => {
  if (model.size === 0) return undefined;
  let start = 0;
  let end = model.size - 1;
  while (start < end) {
    const mid = Math.floor((start + end) / 2);
    const midValue = model.itemAt(mid);
    if (target < midValue) {
      end = mid - 1;
    } else if (target > midValue) {
      start = mid + 1;
    } else {
      return midValue;
    }
  }
  const option1 = model.itemAt(start);
  if (start === 0) return option1;
  const option2 = model.itemAt(start - 1);
  // Option 1 is greater than target, option 2 is smaller.
  return option1 - target < target - option2 ? option1 : option2;
});
//# sourceMappingURL=AbListHelper.js.map
